import React, { useState } from "react"
import PropTypes from "prop-types"
import MomentPropTypes from "react-moment-proptypes"
import DataTable, { tooltipHeaderRenderer } from "@swipesense/data-table"
import { Column, SortDirection } from "react-virtualized"
import { entitiesPropTypes } from "../../utils/propTypes"
import { basicSort } from "../../utils/sort"
import CSVLink from "../CSVLink"
import Loading from "../Loading"
import { ComplianceCircle } from "@swipesense/compliance-helpers"
import "./ExposedLocationList.scss"

const ExposedLocationList = (props) => {
  const dateFormat = "M/D/YYYY • h:mma"

  const [sortBy, setSortBy] = useState("exposureDuration")
  const [sortDirection, setSortDirection] = useState(SortDirection.DESC)
  const [rowClickedProps, setRowClickedProps] = useState({})

  const sortedData = props.rows.sort(basicSort(sortBy, sortDirection))

  const noRowsMessage =
    "No exposed locations detected. Please adjust your case time range to rerun the search."

  const clickRow = ({ rowData }) => {
    setRowClickedProps({
      userId: props.userId,
      name: rowData.name,
      unit: rowData.unit,
      compliance: rowData.compliance,
      entities: props.entities,
      locationId: rowData.id,
      startAt: props.startAt,
      endAt: props.endAt,
    })
  }
  const csvHeaders = [
    { label: "Exposed Room", key: "name" },
    { label: "First Visit", key: "firstContact" },
    { label: "Last Visit", key: "lastContact" },
    { label: "Exposure Duration", key: "exposureDuration" },
    { label: "Compliance", key: "compliance" },
  ]

  const csvDataMap = (location) => ({
    name: location.name,
    firstContact: location.firstContact.format(dateFormat),
    lastContact: location.lastContact.format(dateFormat),
    exposureDuration: location.exposureDuration.humanize(),
    compliance: location.compliance ? `${location.compliance.toFixed(0)}%` : "",
  })

  return (
    <div className="ExposedLocationList">
      <CSVLink
        data={props.rows}
        filename={`${props.caseId}_room_list.csv`}
        headers={csvHeaders}
        mapData={csvDataMap}
      />
      <div className="wrapper">
        <DataTable
          data={sortedData}
          rowHeight={52}
          loading={props.loading}
          loadingComponent={<Loading />}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortChange={(by, dir) => {
            setSortBy(by)
            setSortDirection(dir)
          }}
          onRowClick={clickRow}
          noRowsMessage={noRowsMessage}
        >
          <Column
            label="Exposed Rooms"
            key="room"
            dataKey="name"
            disableSort={true}
            width={0}
          />
          <Column
            label="unit"
            key="unit"
            dataKey="unit"
            disableSort={true}
            width={0}
          />
          <Column
            label="First Visit"
            key="firstContact"
            dataKey="firstContact"
            width={0}
            cellRenderer={({ cellData }) => cellData.format(dateFormat)}
          />
          <Column
            label="Last Visit"
            key="lastContact"
            dataKey="lastContact"
            width={0}
            cellRenderer={({ cellData }) => cellData.format(dateFormat)}
          />
          <Column
            label="Exposure Duration"
            key="exposureDuration"
            dataKey="exposureDuration"
            width={0}
            headerRenderer={tooltipHeaderRenderer(
              '"Exposed" indicates a visit to a room occupied by a contagious patient or employee.'
            )}
            cellRenderer={({ cellData }) => cellData.humanize()}
          />
          <Column
            label="Department"
            key="department"
            dataKey="department"
            disableSort={true}
            width={0}
          />
          <Column
            label="Compliance"
            key="compliance"
            dataKey="compliance"
            width={0}
            headerRenderer={tooltipHeaderRenderer(
              "User's hand hygiene compliance during the entire exposure period."
            )}
            cellRenderer={({ cellData }) =>
              cellData && (
                <div className="room-compliance">
                  <ComplianceCircle compliance={cellData} />
                  {cellData.toFixed(0)}%
                </div>
              )
            }
          />
          <Column label="" key="" dataKey="" disableSort={true} maxWidth={10} />
        </DataTable>
      </div>
      <div className="total-results">
        {props.rows && (
          <div>
            {props.rows.length} {props.rows.length === 1 ? "result" : "results"}
          </div>
        )}
      </div>
      {React.createElement(props.onRowClickComponent, rowClickedProps)}
    </div>
  )
}

ExposedLocationList.propTypes = {
  userId: PropTypes.string,
  startAt: PropTypes.string,
  endAt: PropTypes.string,
  loading: PropTypes.bool,
  entities: entitiesPropTypes,
  caseId: PropTypes.string,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      unit: PropTypes.string,
      department: PropTypes.string,
      exposureDuration: MomentPropTypes.momentDurationObj,
      firstContact: MomentPropTypes.momentObj,
      lastContact: MomentPropTypes.momentObj,
      compliance: PropTypes.float,
    })
  ),
  onRowClickComponent: PropTypes.func,
}

ExposedLocationList.defaultProps = {
  rows: [],
}

export default ExposedLocationList
